// ==================== SMS MESSAGE ANALYTICS COMPONENT ====================

import { motion } from 'framer-motion';
import { MessageSquare, TrendingUp, TrendingDown, Minus, ArrowUpRight, ArrowDownLeft } from 'lucide-react';
import { Card, CardHeader, CardTitle, CardContent } from '../../../components/ui/Card';
import { cn } from '../../../utils/helpers';
import { staggerItem } from '../../../utils/animations';
import type { SmsAnalyticsData } from '../../../services/customer-dashboard.service';

// ==================== TYPES ====================

interface SmsMessageAnalyticsProps {
  data: SmsAnalyticsData | null;
  isLoading?: boolean;
}

// ==================== CHART COLORS ====================

const SMS_CHART_COLORS = {
  sent: '#8b5cf6', // Purple
  delivered: '#22c55e', // Green
  failed: '#ef4444', // Red
};

// ==================== MINI BAR CHART ====================

const SmsMiniBarChart = ({ 
  data, 
  maxHeight = 80,
}: { 
  data: Array<{ date: string; sent: number; delivered: number; failed: number }>;
  maxHeight?: number;
}) => {
  if (!data || data.length === 0) {
    return (
      <div className="h-20 flex items-center justify-center text-(--color-muted-gray) text-sm">
        No SMS data available
      </div>
    );
  }

  const maxValue = Math.max(...data.flatMap(d => [d.sent, d.delivered, d.failed]), 1);

  return (
    <div className="flex items-end gap-1 h-20">
      {data.map((day, index) => {
        const sentHeight = (day.sent / maxValue) * maxHeight;
        const deliveredHeight = (day.delivered / maxValue) * maxHeight;
        const failedHeight = (day.failed / maxValue) * maxHeight;
        
        return (
          <div key={day.date} className="flex-1 flex flex-col items-center gap-0.5 group relative">
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 hidden group-hover:block z-10">
              <div className="bg-(--color-midnight-navy) border border-(--color-border-gray) rounded-lg p-2 text-xs whitespace-nowrap shadow-lg">
                <p className="text-(--color-muted-gray) mb-1">{new Date(day.date).toLocaleDateString()}</p>
                <p className="text-purple-400">Sent: {day.sent}</p>
                <p className="text-green-400">Delivered: {day.delivered}</p>
                <p className="text-red-400">Failed: {day.failed}</p>
              </div>
            </div>
            
            {/* Bars */}
            <div className="w-full flex gap-px">
              <div 
                className="flex-1 rounded-t transition-all duration-300"
                style={{ 
                  height: `${sentHeight}px`, 
                  backgroundColor: SMS_CHART_COLORS.sent,
                  minHeight: day.sent > 0 ? '2px' : '0',
                }}
              />
              <div 
                className="flex-1 rounded-t transition-all duration-300"
                style={{ 
                  height: `${deliveredHeight}px`, 
                  backgroundColor: SMS_CHART_COLORS.delivered,
                  minHeight: day.delivered > 0 ? '2px' : '0',
                }}
              />
              <div 
                className="flex-1 rounded-t transition-all duration-300"
                style={{ 
                  height: `${failedHeight}px`, 
                  backgroundColor: SMS_CHART_COLORS.failed,
                  minHeight: day.failed > 0 ? '2px' : '0',
                }}
              />
            </div>
            
            {/* Date label for first/last/middle */}
            {(index === 0 || index === data.length - 1 || index === Math.floor(data.length / 2)) && (
              <span className="text-[10px] text-(--color-muted-gray) mt-1 truncate w-full text-center">
                {new Date(day.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
              </span>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ==================== STAT PILL ====================

const StatPill = ({
  label,
  value,
  color,
  trend,
}: {
  label: string;
  value: number | string;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}) => (
  <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-(--color-navy-light) border border-(--color-border-gray)">
    <div className={cn('w-2 h-2 rounded-full', color)} />
    <div className="flex-1">
      <p className="text-xs text-(--color-muted-gray)">{label}</p>
      <p className="text-sm font-semibold text-white">{typeof value === 'number' ? value.toLocaleString() : value}</p>
    </div>
    {trend && (
      <div>
        {trend === 'up' && <TrendingUp size={14} className="text-(--color-success)" />}
        {trend === 'down' && <TrendingDown size={14} className="text-(--color-error)" />}
        {trend === 'neutral' && <Minus size={14} className="text-(--color-muted-gray)" />}
      </div>
    )}
  </div>
);

// ==================== COMPONENT ====================

export const SmsMessageAnalytics = ({ 
  data, 
  isLoading = false,
}: SmsMessageAnalyticsProps) => {
  if (isLoading) {
    return (
      <motion.div variants={staggerItem}>
        <Card className="h-full">
          <CardHeader className="border-b-0 pb-1 mb-0">
            <CardTitle className="text-base">SMS Message Analytics</CardTitle>
          </CardHeader>
          <CardContent className="pt-2">
            <div className="h-20 bg-(--color-border-gray) rounded animate-pulse mb-3" />
            <div className="grid grid-cols-4 gap-2">
              {[...Array(4)].map((_, i) => (
                <div key={i} className="h-14 bg-(--color-border-gray) rounded animate-pulse" />
              ))}
            </div>
          </CardContent>
        </Card>
      </motion.div>
    );
  }

  return (
    <motion.div variants={staggerItem}>
      <Card className="h-full">
        <CardHeader className="border-b-0 pb-1 mb-0">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <MessageSquare size={16} className="text-purple-400" />
              SMS Message Analytics
            </CardTitle>
            <div className="flex items-center gap-3 text-xs">
              <div className="flex items-center gap-1 text-(--color-muted-gray)">
                <ArrowUpRight size={12} className="text-blue-400" />
                <span>{data?.byDirection.outbound || 0} out</span>
              </div>
              <div className="flex items-center gap-1 text-(--color-muted-gray)">
                <ArrowDownLeft size={12} className="text-green-400" />
                <span>{data?.byDirection.inbound || 0} in</span>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent className="pt-2">
          {/* Chart */}
          <div className="mb-3">
            {data?.dailyStats && data.dailyStats.length > 0 ? (
              <SmsMiniBarChart data={data.dailyStats} />
            ) : (
              <div className="h-20 flex items-center justify-center border border-dashed border-(--color-border-gray) rounded-lg">
                <div className="text-center">
                  <MessageSquare size={24} className="text-(--color-muted-gray) mx-auto mb-1" />
                  <p className="text-xs text-(--color-muted-gray)">No SMS data for this period</p>
                </div>
              </div>
            )}
          </div>

          {/* Legend */}
          <div className="flex items-center justify-center gap-4 mb-3 text-xs">
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: SMS_CHART_COLORS.sent }} />
              <span className="text-(--color-muted-gray)">Sent</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: SMS_CHART_COLORS.delivered }} />
              <span className="text-(--color-muted-gray)">Delivered</span>
            </div>
            <div className="flex items-center gap-1.5">
              <div className="w-3 h-3 rounded" style={{ backgroundColor: SMS_CHART_COLORS.failed }} />
              <span className="text-(--color-muted-gray)">Failed</span>
            </div>
          </div>

          {/* Stats Grid */}
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-2">
            <StatPill 
              label="Total Sent" 
              value={data?.totals.sent || 0} 
              color="bg-purple-400" 
            />
            <StatPill 
              label="Delivery Rate" 
              value={`${data?.totals.deliveryRate || 0}%`} 
              color="bg-green-400" 
              trend={data && data.totals.deliveryRate >= 90 ? 'up' : data && data.totals.deliveryRate < 70 ? 'down' : 'neutral'}
            />
            <StatPill 
              label="Failed" 
              value={data?.totals.failed || 0} 
              color="bg-red-400" 
              trend={data && data.totals.failed > 0 ? 'down' : 'neutral'}
            />
            <StatPill 
              label="Pending" 
              value={data?.totals.pending || 0} 
              color="bg-yellow-400" 
            />
            <StatPill 
              label="Segments" 
              value={`${data?.totalSegments || 0} (${data?.avgSegmentsPerMessage || 0}/msg)`} 
              color="bg-blue-400" 
            />
          </div>
        </CardContent>
      </Card>
    </motion.div>
  );
};

export default SmsMessageAnalytics;
